'use client';

import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react';
import { format, isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CalendarProps, Calendar } from './calendar.component';
import { datePickerStyles } from './calendar.styles';

import { Label } from '@/components/ui/label.component';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover.component';
import { Icon } from '@/components/ui/icon.component';
import { applyMask } from '@/components/ui/input/input-mask.utils';
import { findScrollBoundary } from '@/components/ui/popover-boundary.util';

const DATE_MASK = '99/99/9999';
const DATE_FORMAT = 'dd/MM/yyyy';
const CALENDAR_COLLISION_PADDING = 8;

function parseDate(value: string): Date | undefined {
  if (value.length < DATE_FORMAT.length) {
    return undefined;
  }

  const parsed = parse(value, DATE_FORMAT, new Date(), { locale: ptBR });

  if (!isValid(parsed) || format(parsed, DATE_FORMAT) !== value) {
    return undefined;
  }

  return parsed;
}

function isWithinRange(date: Date, minDate?: Date, maxDate?: Date): boolean {
  if (minDate && isBefore(date, startOfDay(minDate))) {
    return false;
  }

  return !(maxDate && isAfter(date, startOfDay(maxDate)));
}

export type DatePickerProps = {
  value?: Date;
  onChange?: (date?: Date) => void;
  onBlur?: () => void;
  onValidityChange?: (isValid: boolean) => void;
  label?: string;
  labelId?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  errorMessage?: string;
  error?: string;
  dataTestId?: string;
  className?: string;
  calendarProps?: Omit<CalendarProps, 'mode' | 'selected' | 'onSelect'>;
};

export function DatePicker({
  value,
  onChange,
  onBlur,
  onValidityChange,
  label,
  labelId,
  placeholder = 'dd/mm/aaaa',
  disabled,
  required,
  minDate,
  maxDate,
  errorMessage = 'Data inválida',
  error,
  dataTestId,
  className,
  calendarProps,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ? format(value, DATE_FORMAT) : '');
  const [hasFormatError, setHasFormatError] = useState(false);
  const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const errorId = `${inputId}-error`;

  useEffect(() => {
    setInputValue(value ? format(value, DATE_FORMAT) : '');
    setHasFormatError(false);
  }, [value]);

  useEffect(() => {
    onValidityChange?.(!hasFormatError);
  }, [hasFormatError, onValidityChange]);

  const visibleError = hasFormatError ? errorMessage : error;
  const hasError = Boolean(visibleError);
  const styles = datePickerStyles({ open, hasError, disabled });
  const disabledDays = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(applyMask(event.target.value, DATE_MASK));

    if (hasFormatError) {
      setHasFormatError(false);
    }
  };

  const handleBlur = (): void => {
    onBlur?.();

    if (inputValue === '') {
      setHasFormatError(false);
      if (value) {
        onChange?.(undefined);
      }
      return;
    }

    const parsed = parseDate(inputValue);

    if (!parsed || !isWithinRange(parsed, minDate, maxDate)) {
      setHasFormatError(true);
      return;
    }

    setHasFormatError(false);
    if (!value || format(value, DATE_FORMAT) !== inputValue) {
      onChange?.(parsed);
    }
  };

  const handleSelect = (date?: Date): void => {
    setHasFormatError(false);
    setInputValue(date ? format(date, DATE_FORMAT) : '');
    onChange?.(date);
    setOpen(false);
  };

  const handleOpenChange = (next: boolean): void => {
    if (next) {
      setCollisionBoundary(findScrollBoundary(containerRef.current));
    }

    setOpen(next);
  };

  return (
    <div ref={containerRef} className={styles.container({ className })} data-testid={dataTestId}>
      {label && (
        <Label id={labelId} htmlFor={inputId} className={styles.label()}>
          {label}
        </Label>
      )}

      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className={styles.field()}>
            <input
              id={inputId}
              type="text"
              inputMode="numeric"
              className={styles.input()}
              placeholder={placeholder}
              value={inputValue}
              disabled={disabled}
              required={required}
              onChange={handleInputChange}
              onBlur={handleBlur}
              aria-required={required}
              aria-invalid={hasError}
              aria-describedby={hasError ? errorId : undefined}
              data-testid={dataTestId ? `${dataTestId}-input` : DataTestIdEnum.DATE_PICKER_INPUT}
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                className={styles.iconButton()}
                disabled={disabled}
                aria-label={label ? `Abrir calendário: ${label}` : 'Abrir calendário'}
                data-testid={dataTestId ? `${dataTestId}-trigger` : DataTestIdEnum.DATE_PICKER_TRIGGER}
              >
                <Icon icon={faCalendarDays} className={styles.icon()} aria-hidden />
              </button>
            </PopoverTrigger>
          </div>
        </PopoverAnchor>

        <PopoverContent
          align="end"
          collisionBoundary={collisionBoundary ?? undefined}
          collisionPadding={CALENDAR_COLLISION_PADDING}
          className="w-auto border-0 bg-transparent p-0 shadow-none"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value ?? minDate}
            disabled={disabledDays}
            {...calendarProps}
          />
        </PopoverContent>
      </Popover>

      {visibleError && (
        <span id={errorId} role="alert" className={styles.error()}>
          {visibleError}
        </span>
      )}
    </div>
  );
}
